import React from 'react';
import styled from 'styled-components';

import {
  GOOD_PUP_PRIVACY_LINK,
  GOOD_PUP_TERMS_LINK,
} from '@rover/constants/src/sitter/training.constants';
import { Box, Display, Flex, Heading, Image, Link, Paragraph, Text } from '@rover/kibble/core';
import { PageLayout } from '@rover/react-lib/src/components';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import FloatingFooter from '@rover/react-lib/src/components/utils/FloatingFooter';
import goodPupDownload from '@rover/react-lib/src/images/seo/goodpup_download.jpg';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import DogTrainingButton from '@rover/react-lib/src/pages/landing/SeoLocation/pages/DogTraining/components/DogTrainingButton';
import emitMetricOnCtaClick from '@rover/react-lib/src/pages/landing/ServiceLanding/components/emitMetricOnCtaClick';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AndroidAppStoreButton from '@rover/react-lib/src/pages/shared/AppStoreCallout/AndroidAppStoreButton';
import IosAppStoreButton from '@rover/react-lib/src/pages/shared/AppStoreCallout/IosAppStoreButton';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import { ServiceLandingEnum } from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import GoodPupLogo from '../../components/GoodPupLogo';
import ServiceTargetAudience from '../../components/ServiceTargetAudience';
import ServiceValueProposition from '../../components/ServiceValueProposition';
import TrainingFloatingFooterCTA from '../../components/TrainingFloatingFooterCTA';
import { useGoodPupLink } from '../DogTraining/hooks/useGoodPupLink';
import puppyTrainingHeroImageDesktop from '../shared-images/Hero_Puppy_desktop.jpg';
import puppyTrainingHeroImageMobile from '../shared-images/Hero_Puppy_mobile.jpg';
import puppyTrainingHeroImageTablet from '../shared-images/Hero_Puppy_tablet.jpg';

const puppyTrainingHeroImages: HeroImageSrcSet = {
  desktop: {
    src: puppyTrainingHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: puppyTrainingHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: puppyTrainingHeroImageMobile,
    height: 614,
  },
};

const AppShowCaseImage = styled(Image)`
  // we use max width to prevent a horizontal scroll
  max-width: 100%;
`;

const PuppyTraining: React.FC<Props> = ({
  serviceLandingData,
  citiesAndNeighborhoods,
  serviceLinksWithDisplayName,
  tags,
}) => {
  const serviceLandingType = ServiceLandingEnum.PUPPY_TRAINING;

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;
  const goodPupLink = useGoodPupLink(
    useFlag(FLAGS.ROLLOUT_EMBEDDED_WEB_GOODPUP_LINK),
    tags
  ).toString();
  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <FloatingFooter.ScrollBoundary offsetTop="100vh">
        <LandingHero heroImageSrcSet={puppyTrainingHeroImages}>
          <LandingHero.Content width={['100%', '480px']}>
            <LandingHero.Display mt="0x" mb="4x">
              {h1}
            </LandingHero.Display>
            <LandingHero.Text>{h2}</LandingHero.Text>
            <DogTrainingButton
              href={goodPupLink}
              fullWidth
              size="large"
              variant="primary"
              my="4x"
              onClick={() => {
                emitMetricOnCtaClick({
                  component: 'hero',
                  slug: serviceLandingType || 'unknown',
                });
              }}
            >
              Start free 7-day trial
            </DogTrainingButton>
            <Box textAlign="center" mt="4x">
              <Text textColor="placeholder" size="100">
                GoodPup is owned and operated by PetCareNow, Inc. and use of GoodPup's site or
                services is subject to their{' '}
                <Link
                  href={GOOD_PUP_TERMS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="100"
                >
                  Terms
                </Link>{' '}
                and{' '}
                <Link
                  href={GOOD_PUP_PRIVACY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="100"
                >
                  Privacy Policy
                </Link>
                .
              </Text>
            </Box>
          </LandingHero.Content>
        </LandingHero>

        <PageLayout style={{ marginTop: '0px', marginBottom: '0px' }}>
          <Flex alignItems="center" flexDirection="column">
            <GoodPupLogo />
            <ServiceValueProposition>
              <ServiceValueProposition.Display>
                Virtual puppy training with experienced trainers
              </ServiceValueProposition.Display>
              <ServiceValueProposition.Text textAlign="center">
                Try GoodPup, virtual dog training that really works. It's perfect for everything
                from potty training and teaching the sit skill to more advanced training and curbing
                unwanted behaviors too.
              </ServiceValueProposition.Text>
              <ServiceValueProposition.List>
                <ServiceValueProposition.ListItem>
                  <ServiceValueProposition.Text>
                    Enjoy private puppy training classes with GoodPup. It's run by a team of pet
                    behavior experts and pet parents who understand you and your puppy's needs.
                  </ServiceValueProposition.Text>
                </ServiceValueProposition.ListItem>
                <ServiceValueProposition.ListItem>
                  <ServiceValueProposition.Text>
                    Great for puppies, recently adopted dogs, and curbing that one behavior you wish
                    your pup would stop doing by helping them find a better way to communicate
                    through training.
                  </ServiceValueProposition.Text>
                </ServiceValueProposition.ListItem>
                <ServiceValueProposition.ListItem>
                  <ServiceValueProposition.Text>
                    Train in one-on-one video sessions with experienced puppy trainers from wherever
                    your puppy is most comfortable, from your home to the backyard and beyond.
                  </ServiceValueProposition.Text>
                </ServiceValueProposition.ListItem>
                <ServiceValueProposition.ListItem>
                  <ServiceValueProposition.Text>
                    Get a custom training plan tailored to your puppy's unique needs and around the
                    clock live chat support.
                  </ServiceValueProposition.Text>
                </ServiceValueProposition.ListItem>
              </ServiceValueProposition.List>
            </ServiceValueProposition>
            <HowRoverWorks>
              <HowRoverWorks.Display>How to book puppy training</HowRoverWorks.Display>
              <HowRoverWorks.List>
                <HowRoverWorks.ListItem image={ImageOptions.CHAT}>
                  <HowRoverWorks.ItemHeading step={1}>
                    Tell GoodPup about your puppy
                  </HowRoverWorks.ItemHeading>
                  <HowRoverWorks.ItemContent>
                    Build the right training plan for your puppy by first filling out a short
                    assessment.
                  </HowRoverWorks.ItemContent>
                </HowRoverWorks.ListItem>
                <HowRoverWorks.ListItem image={ImageOptions.CALENDAR}>
                  <HowRoverWorks.ItemHeading step={2}>
                    Book your first training session
                  </HowRoverWorks.ItemHeading>
                  <HowRoverWorks.ItemContent>
                    You can typically get your first video call with an experienced puppy trainer
                    scheduled within 24 hours.
                  </HowRoverWorks.ItemContent>
                </HowRoverWorks.ListItem>
                <HowRoverWorks.ListItem image={ImageOptions.LEASH}>
                  <HowRoverWorks.ItemHeading step={3}>
                    Begin training with GoodPup!
                  </HowRoverWorks.ItemHeading>
                  <HowRoverWorks.ItemContent>
                    Your first video session, 24/7 live chat, and full access to GoodPup is free for
                    your first 7 days.
                  </HowRoverWorks.ItemContent>
                </HowRoverWorks.ListItem>
              </HowRoverWorks.List>
            </HowRoverWorks>
            {/* The extra padding left in each box in this section is because we do not have the capability to use the existing 
          component to have the right spacing. There is a ticket to resolve this issue: DEV-101881 */}
            <ServiceTargetAudience>
              <ServiceTargetAudience.Display>
                Experience the GoodPup difference
              </ServiceTargetAudience.Display>
              <ServiceTargetAudience.List mb="1x">
                <Box paddingLeft="8x" paddingBottom="4x">
                  <Heading mb="1x">One-on-one sessions</Heading>
                  <Paragraph textColor="tertiary">
                    Your private puppy trainer can offer more personalized attention and instruction
                    than a group class can.
                  </Paragraph>
                </Box>
                <Box paddingLeft="8x" paddingBottom="4x">
                  <Heading mb="1x">Training in your home</Heading>
                  <Paragraph textColor="tertiary">
                    A familiar environment like your home can offer your puppy fewer distractions
                    and help them feel more comfortable as they learn new life skills.
                  </Paragraph>
                </Box>
                <Box paddingLeft="8x" paddingBottom="4x">
                  <Heading mb="1x">Personalized training plans</Heading>
                  <Paragraph textColor="tertiary">
                    You'll get a custom training plan that is tailored to your puppy's unique needs
                    and helps you meet your training goals.
                  </Paragraph>
                </Box>
                <Box paddingLeft="8x" paddingBottom="4x">
                  <Heading mb="1x">Experienced dog trainers</Heading>
                  <Paragraph textColor="tertiary">
                    Trainers with GoodPup are certified, vetted, and reviewed and have extensive
                    training, dog behavior, and/or veterinary experience.
                  </Paragraph>
                </Box>
                <Box paddingLeft="8x">
                  <Heading mb="1x">Flexible scheduling</Heading>
                  <Paragraph textColor="tertiary">
                    Set up your training sessions when they work best for your schedule and without
                    the need to drive to a training facility.
                  </Paragraph>
                </Box>
              </ServiceTargetAudience.List>
            </ServiceTargetAudience>

            <Box my="8x" mt="1x" data-qa-id="service-landing-download-app-callout">
              <Flex width="100%" flexDirection="column" justifyContent="center" alignItems="center">
                <Display as="h3" size="500" responsive={false} textAlign="center" mb="6x">
                  Download the GoodPup app
                </Display>
                <AppShowCaseImage
                  src={goodPupDownload}
                  description="A mobile device each displaying booking on the GoodPup app."
                  width="438px" // This value is from the design
                  height="auto"
                />
                <Paragraph paddingTop="8x" size="200" textAlign="center" textColor="tertiary">
                  Available on the web, iOS, and Android
                </Paragraph>
                <Flex
                  width="100%"
                  flexDirection={['column', 'row']}
                  justifyContent="center"
                  alignItems="center"
                  mt="1x"
                >
                  <IosAppStoreButton linkPath={goodPupLink} eventName="training-seo-app-download" />
                  <Box ml={['0x', '12x']} mt={['2x', '0x']}>
                    <AndroidAppStoreButton
                      linkPath={goodPupLink}
                      eventName="training-seo-app-download"
                      target="_self"
                    />
                  </Box>
                </Flex>
              </Flex>
            </Box>
            <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
              <ServiceLandingServices.Display>
                Rover offers many services to meet your pet care needs, including:
              </ServiceLandingServices.Display>
            </ServiceLandingServices>
            <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
              <CitiesAndNeighborhoods.Display>
                Thousands of puppy trainers in cities and neighborhoods, including:
              </CitiesAndNeighborhoods.Display>
            </CitiesAndNeighborhoods>
          </Flex>
        </PageLayout>
        <TrainingFloatingFooterCTA
          serviceLandingType={serviceLandingType}
          goodPupLink={goodPupLink}
        >
          <Paragraph size="200" fontWeight="semibold" mb={['5x', '3x']} maxWidth="100%">
            Ready to try GoodPup Puppy Training?
          </Paragraph>
        </TrainingFloatingFooterCTA>
      </FloatingFooter.ScrollBoundary>
    </>
  );
};

export default PuppyTraining;
